import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { FaPlus, FaEdit, FaTrash, FaLinkedinIn, FaEnvelope, FaPhone } from 'react-icons/fa';

const TeamManagement = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentMember, setCurrentMember] = useState({
    id: null,
    name: '',
    position: '',
    description: '',
    photo_url: '',
    email: '',
    phone: '',
    linkedin_url: '',
    order_index: 0,
    is_active: true
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/team`);
      if (response.data.success) {
        setMembers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editMode) {
        await axios.put(`${API_URL}/team/${currentMember.id}`, currentMember, { headers });
        alert('Team member updated successfully!');
      } else {
        await axios.post(`${API_URL}/team`, currentMember, { headers });
        alert('Team member added successfully!');
      }

      resetForm();
      fetchMembers();
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Failed to save team member');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (member) => {
    setCurrentMember(member);
    setEditMode(true);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/team/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Team member deleted successfully!');
      fetchMembers();
    } catch (error) {
      console.error('Error deleting team member:', error);
      alert('Failed to delete team member');
    }
  };

  const resetForm = () => {
    setCurrentMember({
      id: null,
      name: '',
      position: '',
      description: '',
      photo_url: '',
      email: '',
      phone: '',
      linkedin_url: '',
      order_index: 0,
      is_active: true
    });
    setEditMode(false);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentMember({
      ...currentMember,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  if (loading && !showForm) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-maroon-700"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Team Management</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-maroon-700 hover:bg-maroon-800 text-white px-6 py-3 rounded-lg flex items-center transition-all duration-300"
        >
          <FaPlus className="mr-2" />
          {showForm ? 'Cancel' : 'Add Team Member'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {editMode ? 'Edit Team Member' : 'Add New Team Member'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={currentMember.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="Full Name"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Position *</label>
                <input
                  type="text"
                  name="position"
                  value={currentMember.position}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="e.g. Chief Executive Officer"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Description</label>
              <textarea
                name="description"
                value={currentMember.description}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                placeholder="Brief description or bio..."
              ></textarea>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Photo URL</label>
              <input
                type="text"
                name="photo_url"
                value={currentMember.photo_url}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                placeholder="https://example.com/photo.jpg"
              />
              <p className="text-xs text-gray-500 mt-1">Upload image to server and paste URL here</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={currentMember.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={currentMember.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="+62 xxx xxxx xxxx"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Order Index</label>
                <input
                  type="number"
                  name="order_index"
                  value={currentMember.order_index}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">LinkedIn URL</label>
              <input
                type="text"
                name="linkedin_url"
                value={currentMember.linkedin_url}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                placeholder="https://linkedin.com/in/username"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={currentMember.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-maroon-700 focus:ring-maroon-700 border-gray-300 rounded"
              />
              <label className="ml-2 text-gray-700">Active</label>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading}
                className="bg-maroon-700 hover:bg-maroon-800 text-white px-6 py-3 rounded-lg transition-all duration-300 disabled:bg-gray-400"
              >
                {loading ? 'Saving...' : (editMode ? 'Update Member' : 'Add Member')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-3 rounded-lg transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Team Members Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {members.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-xl shadow-md">
            <p className="text-gray-500">No team members found. Add your first team member!</p>
          </div>
        ) : (
          members.map((member) => (
            <div key={member.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300">
              <div className="relative h-48 bg-gradient-to-br from-maroon-700 to-maroon-900">
                {member.photo_url ? (
                  <img
                    src={member.photo_url}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white text-6xl font-bold">
                    {member.name.charAt(0)}
                  </div>
                )}
                {!member.is_active && (
                  <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs">
                    Inactive
                  </div>
                )}
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-gray-900">{member.name}</h3>
                <p className="text-sm text-maroon-700 font-semibold mb-2">{member.position}</p>
                {member.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{member.description}</p>
                )}
                <div className="flex items-center space-x-2 mb-3 text-sm text-gray-500">
                  {member.email && (
                    <a href={`mailto:${member.email}`} className="hover:text-maroon-700">
                      <FaEnvelope />
                    </a>
                  )}
                  {member.phone && (
                    <a href={`tel:${member.phone}`} className="hover:text-maroon-700">
                      <FaPhone />
                    </a>
                  )}
                  {member.linkedin_url && (
                    <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" className="hover:text-maroon-700">
                      <FaLinkedinIn />
                    </a>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(member)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm transition-all duration-300"
                  >
                    <FaEdit className="inline mr-1" /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(member.id)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm transition-all duration-300"
                  >
                    <FaTrash className="inline mr-1" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TeamManagement;
